import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  PenLine,
  Circle,
  CircleCheck,
  Check,
  Minus,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import {
  ApiError,
  completeTwentyQuestions,
  getTwentyQuestions,
  restartTwentyQuestions,
  saveTwentyAnswer,
  type TwentyQuestion,
} from "@/lib/api";

type ScreenName = "intro" | "question" | "summary";
type YesNo = "yes" | "no";

export default function TwentyQuestions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<TwentyQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, YesNo>>({});
  const [completed, setCompleted] = useState(false);
  const [screen, setScreen] = useState<ScreenName>("intro");
  const [qi, setQi] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTwentyQuestions()
      .then((d) => {
        setQuestions(d.questions);
        setAnswers(d.assessment?.answers ?? {});
        setCompleted(d.assessment?.status === "completed");
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load the questions. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const total = questions.length;
  const answeredCount = Object.keys(answers).length;

  async function answer(value: YesNo, advance = false) {
    const q = questions[qi];
    if (!q) return;
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
    try {
      await saveTwentyAnswer(q.id, value);
      setCompleted(false); // answering re-opens the assessment
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't save your answer.");
      return;
    }
    if (advance) {
      if (qi + 1 < total) setQi(qi + 1);
      else {
        try {
          await completeTwentyQuestions();
          setCompleted(true);
        } catch {
          /* summary still renders from local answers */
        }
        setScreen("summary");
      }
    }
  }

  function begin() {
    if (completed && answeredCount === total) {
      setScreen("summary");
      return;
    }
    let first = 0;
    while (first < total && answers[questions[first]?.id]) first++;
    setQi(Math.min(first, Math.max(total - 1, 0)));
    setScreen("question");
  }

  async function restart() {
    try {
      await restartTwentyQuestions();
    } catch {
      /* non-fatal */
    }
    setAnswers({});
    setCompleted(false);
    setQi(0);
    setScreen("question");
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/app")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const resumed = answeredCount > 0 && answeredCount < total;
  const done = answeredCount === total;

  /* -------------------------------- intro -------------------------------- */
  if (screen === "intro") {
    return (
      <TwentyShell
        onBack={() => navigate("/app")}
        tag={<PrivacyTag />}
        footer={
          <>
            <Button size="xl" fullWidth onClick={begin}>
              {done ? "See Your Answers" : resumed ? "Continue the Questions" : "Begin the Questions"}
            </Button>
            <Button
              variant="ghost"
              fullWidth
              className="mt-2"
              onClick={done ? restart : () => navigate("/steps/1")}
            >
              {done ? "Answer Again" : "Skip to Step 1"}
            </Button>
          </>
        }
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Before Step One · Optional
          </p>
          <h1 className="mt-1.5 text-[22px] font-bold leading-snug text-foreground">
            The Twenty Questions
          </h1>
        </div>
        <p className="text-[15px] leading-relaxed text-foreground">
          Twenty honest yes-or-no questions, developed at Johns Hopkins University
          Hospital in the 1930s as a self-appraisal. Many people answer them before
          starting Step 1. There's no score to beat — only an honest look.
        </p>
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <IconChip icon={Lock} tint="primary" />
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Your answers are private, saved as you go, and never part of a score or
              streak. You can stop at any time.
            </p>
          </CardContent>
        </Card>
        {resumed && (
          <Card>
            <CardContent className="flex items-center gap-3 px-4 py-3.5">
              <IconChip icon={PenLine} tint="primary" />
              <p className="text-[13px] text-foreground">
                You left off at question {answeredCount + 1}. Your answers so far are
                saved.
              </p>
            </CardContent>
          </Card>
        )}
        <p className="text-xs leading-relaxed text-muted-foreground">
          Not a medical evaluation, and not fellowship literature — a widely used
          self-appraisal, offered here as a starting point. It asks about alcohol
          specifically, so it won't fit every fellowship.
        </p>
      </TwentyShell>
    );
  }

  /* ------------------------------ question ------------------------------- */
  if (screen === "question") {
    const q = questions[qi];
    const value = q ? answers[q.id] : undefined;
    return (
      <TwentyShell
        onBack={() => (qi > 0 ? setQi(qi - 1) : setScreen("intro"))}
        saved
        footer={
          <>
            <Button size="xl" fullWidth disabled={!value} onClick={() => answer(value!, true)}>
              Continue
            </Button>
            <Button variant="ghost" fullWidth className="mt-2" onClick={() => setScreen("intro")}>
              Save & Exit
            </Button>
          </>
        }
      >
        <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          The Twenty Questions · {qi + 1} of {total}
        </p>
        <h1 className="text-[21px] font-bold leading-snug text-foreground">
          {q?.question_text}
        </h1>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Answer as honestly as you can. No one sees this.
        </p>
        <div className="flex flex-col gap-2.5">
          <ChoiceRow on={value === "yes"} title="Yes" onClick={() => answer("yes")} />
          <ChoiceRow on={value === "no"} title="No" onClick={() => answer("no")} />
        </div>
      </TwentyShell>
    );
  }

  /* ------------------------------- summary ------------------------------- */
  const yesList = questions.filter((q) => answers[q.id] === "yes");
  const shown = showAll ? questions : yesList;
  return (
    <TwentyShell
      onBack={() => setScreen("intro")}
      tag={<PrivacyTag />}
      footer={
        <>
          <Button size="xl" fullWidth onClick={() => navigate("/steps/1")}>
            Continue to Step 1
          </Button>
          <Button variant="ghost" fullWidth className="mt-2" onClick={() => setScreen("intro")}>
            Back to Start
          </Button>
        </>
      }
    >
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          The Twenty Questions · Where you landed
        </p>
        <h1 className="mt-1.5 text-[21px] font-bold leading-snug text-foreground">
          {yesList.length === 0
            ? "You answered no to all twenty."
            : `You answered yes to ${yesList.length} of the twenty.`}
        </h1>
      </div>
      <p className="text-[15px] leading-relaxed text-foreground">
        {yesList.length === 0
          ? "If any of these questions gave you pause, that pause is worth noticing. This is here whenever you want to look again."
          : "The traditional guide is plain: yes to even one question is a warning; yes to two or three, the chances are real. However you read your answers, the fact that you answered them honestly is what matters — and it's exactly where Step 1 begins."}
      </p>
      {(yesList.length > 0 || showAll) && (
        <Card>
          <CardContent className="flex flex-col gap-2.5 p-4">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              {showAll ? "All twenty answers" : "Your yes answers"}
            </p>
            {shown.map((q) => (
              <div key={q.id} className="flex items-start gap-2">
                <span
                  className={`mt-0.5 inline-flex ${
                    answers[q.id] === "yes" ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {answers[q.id] === "yes" ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={2} />
                  ) : (
                    <Minus className="h-3.5 w-3.5" strokeWidth={2} />
                  )}
                </span>
                <p className="flex-1 text-sm leading-normal text-foreground">
                  {q.question_text}
                  {showAll && (
                    <span className="text-xs text-muted-foreground">
                      {" "}
                      — {answers[q.id] === "yes" ? "yes" : answers[q.id] === "no" ? "no" : "—"}
                    </span>
                  )}
                </p>
                <button
                  type="button"
                  aria-label="Revisit this answer"
                  className="text-muted-foreground"
                  onClick={() => {
                    setQi(questions.findIndex((x) => x.id === q.id));
                    setScreen("question");
                  }}
                >
                  <PenLine className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
            <Button
              variant="link"
              className="h-auto self-start p-0 text-[13px]"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Show Only Yes Answers" : "See All Twenty Answers"}
            </Button>
          </CardContent>
        </Card>
      )}
      <p className="text-xs leading-relaxed text-muted-foreground">
        This isn't a diagnosis. If you want to talk it through, your sponsor is a good
        place to start.
      </p>
    </TwentyShell>
  );
}

/* ------------------------------ primitives ------------------------------ */

function PrivacyTag() {
  return (
    <span className="inline-flex items-center gap-1.5 pr-3 text-xs text-muted-foreground">
      <Lock className="h-3 w-3" strokeWidth={2} />
      Private — only you can see this
    </span>
  );
}

function TwentyShell({
  onBack,
  tag,
  saved,
  children,
  footer,
}: {
  onBack?: () => void;
  tag?: React.ReactNode;
  saved?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col pb-8">
      <div className="flex min-h-10 items-center justify-between px-3 pt-4">
        {onBack ? (
          <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </Button>
        ) : (
          <span />
        )}
        {saved ? <span className="pr-3 text-xs text-muted-foreground">Saved</span> : tag ?? null}
      </div>
      <div className="flex flex-1 animate-fade-rise flex-col gap-4 px-6 pt-4">{children}</div>
      {footer && <div className="px-6 pt-4">{footer}</div>}
    </div>
  );
}

function ChoiceRow({
  on,
  title,
  onClick,
}: {
  on: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`flex min-h-14 w-full items-center gap-3 rounded-lg border-2 px-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
        on ? "border-primary bg-primary/[0.06]" : "border-border hover:border-primary/50"
      }`}
    >
      <span className={`inline-flex ${on ? "text-primary" : "text-muted-foreground"}`}>
        {on ? (
          <CircleCheck className="h-[18px] w-[18px]" strokeWidth={2} />
        ) : (
          <Circle className="h-[18px] w-[18px]" strokeWidth={2} />
        )}
      </span>
      <span className="text-[15px] font-medium text-foreground">{title}</span>
    </button>
  );
}
